import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const FADE_DURATION_MS = 300;

const DEFAULT_PHRASES = [
{ title: "Casa pulita, semplice.", subtitle: "Pulizie domestiche dalla tua zona, in 24 ore." },
{ title: "Casa riparata, semplice.", subtitle: "Idraulici ed elettricisti vicino a te, oggi." },
{ title: "Casa rinnovata, semplice.", subtitle: "Imbianchini e ristrutturazioni della tua città." },
{ title: "Casa illuminata, semplice.", subtitle: "Impianti elettrici a regola d'arte, paghi solo se accetti." }];


const HeroSection = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const [phrases, setPhrases] = useState(null);
  const [intervalMs, setIntervalMs] = useState(4500);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [imgWidth, setImgWidth] = useState('full');
  const [imgHeight, setImgHeight] = useState('16:9');
  const [imgObjectFit, setImgObjectFit] = useState('cover');
  const [heroPct, setHeroPct] = useState(100);

  const [professions, setProfessions] = useState([]);
  const [filteredProfessions, setFilteredProfessions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase?.from('landing_config')?.select('key, value')?.in('key', ['hero_phrases', 'hero_interval_ms', 'hero_image_url', 'hero_image_width', 'hero_image_height', 'hero_image_object_fit', 'hero_image_size_pct']);

        if (error) return;

        let parsedPhrases = null;

        data?.forEach((row) => {
          if (row?.key === 'hero_phrases') {
            try {
              const parsed = JSON.parse(row?.value);
              if (Array.isArray(parsed) && parsed?.length > 0) {
                if (typeof parsed?.[0] === 'string') {
                  parsedPhrases = parsed?.map((str) => ({
                    title: str,
                    subtitle: "Preventivi gratuiti dai professionisti verificati della tua zona."
                  }));
                } else {
                  parsedPhrases = parsed;
                }
              }
            } catch (_) {}
          } else if (row?.key === 'hero_interval_ms') {
            const ms = parseInt(row?.value, 10);
            if (!isNaN(ms) && ms > 0) {
              setIntervalMs(ms);
            }
          } else if (row?.key === 'hero_image_url') {
            if (row?.value) {
              setHeroImageUrl(row?.value);
            }
          } else if (row?.key === 'hero_image_width') {
            if (row?.value) setImgWidth(row?.value);
          } else if (row?.key === 'hero_image_height') {
            if (row?.value) setImgHeight(row?.value);
          } else if (row?.key === 'hero_image_object_fit') {
            if (row?.value) setImgObjectFit(row?.value);
          } else if (row?.key === 'hero_image_size_pct') {
            const pct = parseInt(row?.value, 10);
            if (!isNaN(pct) && pct > 0) setHeroPct(pct);
          }
        });

        setPhrases(parsedPhrases ?? DEFAULT_PHRASES);
      } catch (_) {
        setPhrases(DEFAULT_PHRASES);
      }
      // Reveal the title after config is resolved (with real or fallback phrases)
      setVisible(true);
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const { data, error } = await supabase?.
        from('professions')?.
        select('id, name, category')?.
        order('name', { ascending: true });
        if (!error && Array.isArray(data)) {
          setProfessions(data);
        }
      } catch (_) {}
    };

    fetchProfessions();
  }, []);

  useEffect(() => {
    if (!phrases) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % (phrases?.length ?? 1));
        setVisible(true);
      }, FADE_DURATION_MS);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [intervalMs, phrases]);

  useEffect(() => {
    if (query?.trim() === '') {
      setFilteredProfessions([]);
      setShowAutocomplete(false);
      return;
    }
    const q = query?.trim()?.toLowerCase();
    const filtered = professions?.
    filter(
      (p) =>
      p?.name?.toLowerCase()?.includes(q) ||
      p?.category?.toLowerCase()?.includes(q)
    )?.slice(0, 8);
    setFilteredProfessions(filtered);
    setShowAutocomplete(filtered?.length > 0);
  }, [query, professions]);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!query?.trim()) {
      navigate('/registrati-cliente');
      return;
    }
    navigate('/cliente/richiesta/nuova', {
      state: { selectedProfession: { id: null, name: query?.trim() } }
    });
  };

  const handleProfessionSelect = (profession) => {
    setQuery(profession?.name);
    setShowAutocomplete(false);
    navigate('/cliente/richiesta/nuova', {
      state: {
        selectedProfession: { id: profession?.id, name: profession?.name }
      }
    });
  };

  const handleInputChange = (e) => {
    setQuery(e?.target?.value);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowAutocomplete(false);
    }, 150);
  };

  const currentPhrase = phrases?.[phraseIndex];

  const aspectRatioStyle = {
    '16:9': { aspectRatio: '16/9' },
    '4:3': { aspectRatio: '4/3' },
    '1:1': { aspectRatio: '1' }
  }?.[imgHeight] || { aspectRatio: '16/9' };

  const optimizedUrl = heroImageUrl ?
  `${heroImageUrl}?width=1400&quality=80&format=webp` :
  null;

  return (
    <section className="w-full bg-[#f8fafc] pt-20 md:pt-28 pb-0">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 text-center">

        <h1 className="text-5xl md:text-6xl lg:text-7xl text-[#013776] leading-[1.1] tracking-tight" style={{ fontWeight: 800 }}>
          <span
            style={{
              opacity: visible ? 1 : 0,
              transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
              display: 'inline'
            }}>

            {currentPhrase?.title}
          </span>
        </h1>

        <p
          className="text-lg md:text-xl text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed"
          style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`
          }}>

          {currentPhrase?.subtitle}
        </p>

        <form onSubmit={handleSearch} className="mt-10 mx-auto" style={{ width: '100%', maxWidth: '580px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              width: '100%',
              maxWidth: '580px',
              margin: '0 auto',
              background: 'white',
              border: '1.5px solid #d1dbe8',
              borderRadius: '12px',
              overflow: 'visible',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>

            <div style={{ position: 'relative', flex: 1, overflow: 'visible' }}>
              <input
                type="text"
                value={query}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={(e) => e?.key === 'Enter' && handleSearch(e)}
                placeholder="Es. elettricista, idraulico, giardiniere..."
                style={{
                  width: '100%',
                  height: '58px',
                  padding: '0 20px',
                  fontSize: '17px',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  borderRadius: '12px 0 0 12px'
                }} />

              {showAutocomplete && filteredProfessions?.length > 0 &&
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  background: 'white',
                  border: '1px solid #d1dbe8',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  maxHeight: '240px',
                  overflowY: 'auto'
                }}>

                  {filteredProfessions?.map((profession) =>
                <div
                  key={profession?.id}
                  onMouseDown={() => handleProfessionSelect(profession)}
                  style={{
                    padding: '10px 20px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderBottom: '1px solid #f0f4f8'
                  }}
                  onMouseEnter={(e) => {e.currentTarget.style.background = '#f5f8fc';}}
                  onMouseLeave={(e) => {e.currentTarget.style.background = 'white';}}>

                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>
                        {profession?.name}
                      </div>
                      {profession?.category &&
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          {profession?.category}
                        </div>
                  }
                    </div>
                )}
                </div>
              }
            </div>
            <button
              type="submit"
              style={{
                height: '58px',
                minWidth: '100px',
                padding: '0 28px',
                background: '#104d98',
                color: 'white',
                fontSize: '17px',
                fontWeight: 700,
                border: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                flexShrink: 0,
                borderRadius: '0 12px 12px 0'
              }}>

              Cerca
            </button>
          </div>
        </form>

        <p className="mt-5 text-sm text-gray-500">Confronti gratuiti · Nessun intermediario

        </p>
      </div>

      <div className="relative mt-12 md:mt-16 w-full pb-0 flex justify-center px-4 md:px-6">
        <div
          style={{
            width: `${heroPct}%`,
            maxWidth: '100%',
            flexShrink: 0
          }}>

          <div
            className="relative overflow-hidden"
            style={{
              ...aspectRatioStyle,
              borderRadius: '16px'
            }}>

            {optimizedUrl ?
            <img
              src={optimizedUrl}
              alt="Professionista in casa"
              style={{ width: '100%', height: '100%', objectFit: imgObjectFit, display: 'block' }} /> :


            <div
              style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(180deg, #f8fafc 0%, #e8eff8 100%)',
                display: 'block'
              }} />

            }
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;